import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Send, MessageCircle } from "lucide-react";
import { api, formatApiError } from "@/lib/auth";
import { fmtDateTime, fmtDate } from "@/lib/datetime";
import { toast } from "sonner";

const CATEGORIES = [
  { key: "compte", label: "Question sur mon compte" },
  { key: "administratif", label: "Question administrative" },
  { key: "site", label: "Problème avec le site" },
  { key: "generale", label: "Question générale" },
  { key: "suggestion", label: "Suggestion / retour" },
  { key: "mot", label: "Un petit mot pour Charbonier Studio 🙂" },
];

const STATUS_LABEL = {
  nouveau: "Envoyé",
  lu: "Envoyé",
  repondu: "Répondu",
  clos: "Clos",
};
const STATUS_TONE = {
  nouveau: "text-[#0E7C90]",
  lu: "text-[#0E7C90]",
  repondu: "text-emerald-700",
  clos: "text-neutral-500",
};

export default function ContactStudio() {
  const [threads, setThreads] = useState([]);
  const [category, setCategory] = useState("compte");
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [reply, setReply] = useState("");
  const [replySending, setReplySending] = useState(false);

  const active = threads.find((t) => t.id === activeId) || null;

  const load = async () => {
    const { data } = await api.get("/support");
    setThreads(data);
  };

  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!message.trim()) { toast.error("Écrivez un message"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/support", { category, message: message.trim() });
      toast.success("Votre message a bien été envoyé à Charbonier Studio.");
      setMessage("");
      setActiveId(data.id);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  const openThread = async (t) => {
    setActiveId(t.id);
    setReply("");
    if ((t.unread_client || 0) > 0) {
      try { await api.post(`/support/${t.id}/mark_read`); } catch {}
    }
  };

  const sendReply = async () => {
    if (!reply.trim() || !active) return;
    setReplySending(true);
    try {
      const { data } = await api.post(`/support/${active.id}/reply`, { text: reply.trim() });
      setReply("");
      toast.success("Réponse envoyée à Charbonier Studio.");
      setThreads((prev) => prev.map((t) => (t.id === data.id ? data : t)));
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setReplySending(false); }
  };

  return (
    <div className="cs-cream min-h-[80vh] py-14" data-testid="page-contact-studio">
      <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
        <Link to="/espace" className="text-sm font-mono uppercase tracking-[0.16em] text-neutral-500 hover:text-black inline-flex items-center gap-2" data-testid="contact-back">
          <ArrowLeft size={14} /> Retour à mon espace
        </Link>

        <div className="mt-4 flex items-start gap-4">
          <div className="w-12 h-12 border border-black/70 flex items-center justify-center shrink-0">
            <MessageCircle size={20} />
          </div>
          <div>
            <div className="cs-eyebrow text-[#0E7C90]">Questions générales & assistance</div>
            <h1 className="cs-h1 mt-2 text-4xl lg:text-5xl">Contacter le studio</h1>
          </div>
        </div>

        <div className="mt-6 max-w-2xl space-y-3 text-neutral-700 leading-relaxed">
          <p>Pour toute question générale, administrative ou concernant le fonctionnement du site, vous pouvez écrire directement à Charbonier Studio ici.</p>
          <p className="text-[13px] font-mono text-neutral-500 uppercase tracking-[0.12em] border-l-2 border-[#0E7C90] pl-4">
            Pour une question liée à une commande en cours, utilisez directement la messagerie de cette commande.
          </p>
          <p className="text-[13px] font-mono text-neutral-500 uppercase tracking-[0.12em] border-l-2 border-[#0E7C90] pl-4">
            Vous souhaitez démarrer un nouveau projet ? Utilisez « Nouvelle commande » afin que nous puissions recueillir toutes les informations nécessaires.
          </p>
        </div>

        <section className="mt-10 grid lg:grid-cols-[440px_1fr] gap-10">
          {/* New message form */}
          <div>
            <div className="cs-eyebrow text-neutral-500">Nouveau message</div>
            <h2 className="cs-h3 mt-2 text-2xl">Écrire à Charbonier Studio</h2>
            <div className="mt-4 space-y-4">
              <label className="block">
                <span className="cs-eyebrow text-neutral-500">Motif du message</span>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="cs-input mt-2 w-full"
                  data-testid="contact-category"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c.key} value={c.key}>{c.label}</option>
                  ))}
                </select>
              </label>
              <label className="block">
                <span className="cs-eyebrow text-neutral-500">Votre message</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={6}
                  className="cs-input mt-2 w-full resize-none"
                  placeholder="Écrivez votre message ici…"
                  data-testid="contact-message"
                />
              </label>
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-mono text-neutral-500">Nous vous répondons directement dans cette page — et par email.</div>
                <button
                  onClick={submit}
                  disabled={sending || !message.trim()}
                  className="cs-btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-submit"
                  aria-label="Envoyer le message"
                >
                  <Send size={14} /> Envoyer mon message
                </button>
              </div>
              <p className="text-[11px] font-mono text-neutral-500 leading-relaxed mt-3" data-testid="contact-rgpd">
                Vos données sont utilisées uniquement pour vous répondre.
                Voir la <Link to="/confidentialite" className="cs-link-underline">politique de confidentialité</Link>.
              </p>
            </div>
          </div>

          {/* History + active thread */}
          <div>
            <div className="cs-eyebrow text-neutral-500">Historique</div>
            <h2 className="cs-h3 mt-2 text-2xl">Vos messages précédents</h2>

            {threads.length === 0 ? (
              <div className="mt-4 border border-dashed border-black/25 p-6 text-neutral-500 text-sm" data-testid="contact-empty">
                Vous n'avez encore envoyé aucun message général. Utilisez le formulaire à gauche.
              </div>
            ) : (
              <div className="mt-4 border-t border-black/15">
                {threads.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => openThread(t)}
                    data-testid={`contact-thread-${t.id}`}
                    className={`w-full text-left border-b border-black/15 py-4 px-3 text-sm transition-colors ${activeId === t.id ? "bg-white" : "hover:bg-white"}`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="font-mono text-[11px] text-neutral-500">{fmtDate(t.created_at)}</span>
                      <span className={`font-mono text-[11px] uppercase tracking-[0.14em] ${STATUS_TONE[t.status] || "text-neutral-500"}`}>
                        {STATUS_LABEL[t.status] || t.status}
                        {(t.unread_client || 0) > 0 && <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] bg-[#0E7C90] text-[#FAF8F5] rounded-full">{t.unread_client}</span>}
                      </span>
                    </div>
                    <div className="font-serif text-[16px] mt-1">{t.category_label}</div>
                    <div className="text-neutral-600 text-[13px] mt-1 line-clamp-2">{t.initial_message}</div>
                  </button>
                ))}
              </div>
            )}

            {active && (
              <div className="mt-8 border border-black/15 bg-white" data-testid="contact-active-thread">
                <div className="px-4 py-3 border-b border-black/10 flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="cs-eyebrow text-neutral-500">Conversation · {fmtDate(active.created_at)}</div>
                    <div className="font-serif text-lg mt-1">{active.category_label}</div>
                  </div>
                  <span className={`font-mono text-[11px] uppercase tracking-[0.14em] ${STATUS_TONE[active.status] || "text-neutral-500"}`}>
                    {STATUS_LABEL[active.status] || active.status}
                  </span>
                </div>
                <div className="p-4 space-y-3 max-h-[50vh] overflow-y-auto">
                  {active.messages.map((m, i) => {
                    const mine = m.author_role === "client";
                    return (
                      <div key={i} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[85%] px-4 py-3 text-[14px] leading-relaxed ${mine ? "bg-[#0E7C90] text-[#FAF8F5]" : "bg-[#FAF8F5] border border-black/15"}`}>
                          <div className={`font-mono text-[10px] uppercase tracking-[0.16em] mb-1 ${mine ? "text-[#FAF8F5]/70" : "text-neutral-500"}`}>
                            {m.author_name} · {fmtDateTime(m.created_at)}
                          </div>
                          <div className="whitespace-pre-wrap">{m.text}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                {active.status !== "clos" ? (
                  <div className="p-4 border-t border-black/10">
                    <textarea
                      value={reply}
                      onChange={(e) => setReply(e.target.value)}
                      rows={3}
                      placeholder="Poursuivre la conversation…"
                      className="cs-input w-full resize-none"
                      data-testid="contact-reply-input"
                    />
                    <div className="mt-2 flex justify-end">
                      <button
                        onClick={sendReply}
                        disabled={replySending || !reply.trim()}
                        className="cs-btn-primary !py-2 !px-4 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                        data-testid="contact-reply-send"
                      >
                        <Send size={14} /> Envoyer
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 border-t border-black/10 text-[13px] font-mono text-neutral-500">
                    Cette conversation a été clôturée par Charbonier Studio.
                  </div>
                )}
              </div>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}
